import {
	CircularProgress,
	FormControl,
	Grid,
	MenuItem,
	OutlinedInput,
	Select,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";

import { getAllTenantAction } from "../../Actions/TenantActions";
import { getTransactionHistoryDailyChartAction } from "../../Actions/TransactionActions";
import { SelectDropdown_SX } from "../../Assets/customTheme/constants";
import CoustomTooltip from "../../Assets/customTheme/customtooltip";
import { TableReset } from "../../Assets/svg/svgicon";
import CustomSnackbar from "../Common/snackBar";
import NoDataFound from "../Dashboard/NoDataFound";
import DateRangePicker from "../Common/DateRangePicker";
import { Line } from "react-chartjs-2";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
		},
	},
};

const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);
	return { from: start, to: end };
};

const MonthlyTransactionReportModel = ({
	globalstartDate,
	globalendDate,
	globalSelectedMerchant,
}) => {
	const [chartDynamicData, setChartDynamicData] = useState({
		labels: [],
		datasets: [],
		fullDates: [],
	});
	const [loading, setLoading] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "error",
	});

	// Date range state using the same pattern as MailLogsSection
	const [selectedRange, setSelectedRange] = useState(getDefaultDateRange());
	const [startDate, setStartDate] = useState(
		getDefaultDateRange().from.toISOString()
	);
	const [endDate, setEndDate] = useState(
		getDefaultDateRange().to.toISOString()
	);

	const [isDailyPaymentFilterModified, setIsDailyPaymentFilterModified] =
		useState(false);
	const [merchantList, setMerchantList] = useState([]);
	const [selectedMerchant, setSelectedMerchant] = useState("");

	const fetchTransactionData = useCallback( async () => {
		if (!startDate || !endDate) return;

		setLoading(true);
		try {
			const params = {
				startDate: startDate,
				endDate: endDate,
				merchantId: selectedMerchant,
			};
			const res = await getTransactionHistoryDailyChartAction(params);
			const transactions = res?.data;
			const labels = transactions?.map(
				(t) =>
					t.transactionDate?.match(/Week \d+/)?.[0] ||
					t.transactionDate
			);
			const fullDates = transactions?.map((t) => t.transactionDate);
			const totalTransactions = transactions?.map(
				(t) => t.totalTransactions
			);
			const totalAmount = transactions?.map(
				(t) => parseFloat(t.totalAmount) || 0
			);

			setChartDynamicData({
				labels: labels || [],
				datasets: [
					{
						label: "Total Transactions",
						data: totalTransactions || [],
						borderColor: "#344D95",
						backgroundColor: "rgba(52, 77, 149, 0.1)",
						borderWidth: 3,
						fill: false,
						tension: 0.4,
						pointRadius: 5,
						pointHoverRadius: 7,
						pointBackgroundColor: "#344D95",
						yAxisID: 'y',
					},
					{
						label: "Total Amount ($)",
						data: totalAmount || [],
						borderColor: "#ffa726",
						backgroundColor: "rgba(255, 167, 38, 0.1)",
						borderWidth: 3,
						fill: false,
						tension: 0.4,
						pointRadius: 5,
						pointHoverRadius: 7,
						pointBackgroundColor: "#ffa726",
						yAxisID: 'y1',
					},
				],
				fullDates: fullDates || [],
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message:
					error?.response?.data?.message ||
					"Something went wrong. Please try again later.",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	},[startDate,endDate,selectedMerchant]);

	useEffect(() => {
		const defaultRange = getDefaultDateRange();

		const isDateRangeModified = !(
			selectedRange?.from &&
			selectedRange?.to &&
			dayjs(selectedRange.from).isSame(dayjs(defaultRange.from), "day") &&
			dayjs(selectedRange.to).isSame(dayjs(defaultRange.to), "day")
		);

		const isMerchantModified = selectedMerchant !== "";
		setIsDailyPaymentFilterModified(
			isDateRangeModified || isMerchantModified
		);
	}, [selectedRange, selectedMerchant]);

	useEffect(() => {
		if (globalstartDate && globalendDate) {
			const start = new Date(globalstartDate);
			const end = new Date(globalendDate);
			setSelectedRange({ from: start, to: end });
			setStartDate(start.toISOString());
			setEndDate(end.toISOString());
		}
	}, [globalstartDate, globalendDate]);

	useEffect(() => {
		if (globalSelectedMerchant) {
			setSelectedMerchant(globalSelectedMerchant);
		} else {
			setSelectedMerchant("");
		}
	}, [globalSelectedMerchant]);

	useEffect(() => {
		getMerchantListData();
	}, []);

	useEffect(() => {
		if (startDate && endDate) {
			fetchTransactionData();
		}
	}, [startDate, endDate,fetchTransactionData]);



	const getMerchantListData = async () => {
		await getAllTenantAction()
			.then((res) => {
				const { data } = res;
				setMerchantList(data.data);
			})
			.catch((error) => {
				setSnackbar({
					open: true,
					message:
						error?.response?.data?.message ||
						"Something went wrong. Please try again later.",
					severity: "error",
				});
			});
	};

	// Handle date range change using the same pattern as MailLogsSection
	const handleRangeChange = (range) => {
		if (!range) {
			setSelectedRange({ from: undefined, to: undefined });
			setStartDate(null);
			setEndDate(null);
			return;
		}

		setSelectedRange(range);

		if (range.from) {
			const year = range.from.getFullYear();
			const month = range.from.getMonth();
			const day = range.from.getDate();
			const startDateObj = new Date(
				Date.UTC(year, month, day, 0, 0, 0, 0)
			);
			setStartDate(startDateObj.toISOString());
		}

		if (range.to) {
			const year = range.to.getFullYear();
			const month = range.to.getMonth();
			const day = range.to.getDate();
			const endDateObj = new Date(
				Date.UTC(year, month, day, 23, 59, 59, 999)
			);
			setEndDate(endDateObj.toISOString());
		}
	};

	const handleMerchantChange = (event) => {
		const newMerchantValue =
			event.target.value === "All merchants" ? "" : event.target.value;
		setSelectedMerchant(newMerchantValue);
	};

	const handleResetFilters = () => {
		const defaultRange = getDefaultDateRange();

		setSelectedRange(defaultRange);
		setStartDate(defaultRange.from.toISOString());
		setEndDate(defaultRange.to.toISOString());
		setSelectedMerchant("");
	};

	const hasData = chartDynamicData?.labels?.length > 0;

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			mode: 'index',
			intersect: false,
		},
		plugins: {
			legend: {
				position: "top",
			},
			title: {
				display: false,
			},
			tooltip: {
				callbacks: {
					title: function (context) {
						const index = context[0].dataIndex;
						return chartDynamicData.fullDates[index] || context[0].label;
					},
					label: function (context) {
						let label = context.dataset.label || '';
						if (label) {
							label += ': ';
						}
						if (context.dataset.label === "Total Amount ($)") {
							label += `$${context.parsed.y?.toFixed(2)}`;
						} else {
							label += context.parsed.y;
						}
						return label;
					},
				},
			},
		},
		scales: {
			x: {
				display: true,
				title: {
					display: false,
				},
			},
			y: {
				type: 'linear',
				display: true,
				position: 'left',
				title: {
					display: true,
					text: 'Total Transactions',
				},
			},
			y1: {
				type: 'linear',
				display: true,
				position: 'right',
				title: {
					display: true,
					text: 'Total Amount ($)',
				},
				ticks: {
					callback: function (value) {
						return `$${value?.toFixed(2)}`;
					},
				},
				grid: {
					drawOnChartArea: false,
				},
			},
		},
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={12}>
				<div className="card">
					<div className="card-body" style={{ height: "100%" }}>
						<div className="row align-items-center pb-10">
							<div className="col-auto mb-5">
								<h2 className="chart-title mb-5">
									Payment Report
								</h2>
							</div>

							<div className="col-md-6 col-lg-4 col-xl-3 col-xxl-2 mb-5 ms-auto">
								<FormControl className="w-100">
									<Select
										labelId="merchant-select-label"
										id="merchant-select"
										value={
											selectedMerchant || "All merchants"
										}
										label="Select Tenant"
										onChange={handleMerchantChange}
										sx={SelectDropdown_SX}
										MenuProps={MenuProps}
										input={<OutlinedInput />}
									>
										<MenuItem value="All merchants">
											All Tenant
										</MenuItem>
										{merchantList?.map(
											(merchant, index) => (
												<MenuItem
													key={index}
													value={merchant.id}
												>
													{merchant.companyName}
												</MenuItem>
											)
										)}
									</Select>
								</FormControl>
							</div>

							<div className="col-md-6 col-lg-4 col-xl-3 col-xxl-2 mb-5">
								<DateRangePicker
									selectedRange={selectedRange}
									onRangeChange={handleRangeChange}
									maxDate={new Date()}
									placeholder="Select date range"
								/>
							</div>

							<div className="col-auto mb-5">
								<CoustomTooltip
									title="Reset"
									placement="top"
									arrow
								>
									<span>
										<button
											className="btn btn-light text-primary btn-icon"
											onClick={handleResetFilters}
											disabled={
												!isDailyPaymentFilterModified
											}
										>
											<TableReset />
										</button>
									</span>
								</CoustomTooltip>
							</div>
						</div>

						<div style={{ height: "350px" }}>
							{loading ? (
								<Typography align="center" sx={{ mt: 10 }}>
									<CircularProgress />
								</Typography>
							) : hasData ? (
								<Line
									data={chartDynamicData}
									options={chartOptions}
								/>
							) : (
								<NoDataFound />
							)}
						</div>
					</div>
				</div>
			</Grid>

			<CustomSnackbar
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={handleCloseSnackbar}
			/>
		</Grid>
	);
};

export default MonthlyTransactionReportModel;
